import { Avatar, createStyles, Grid, withStyles, WithStyles } from '@material-ui/core';
import AwesomeDebouncePromise from 'awesome-debounce-promise';
import { FormikActions, getIn } from 'formik';
import * as _ from 'lodash';
import { inject } from 'mobx-react';
import * as React from 'react';
import Button from 'src/common/Button/Button';
import ESDialog from 'src/common/ESDialog';
import FullWidthField from 'src/common/FullWidthField';
import SelectField from 'src/common/SelectField/SelectField';
import { IOrganization } from 'src/Definitions';
import { IUser } from 'src/Definitions';
import { IStores } from 'src/Stores';
import { IRole } from 'src/stores/RolesStore/RolesStore';
import * as Yup from 'yup';

interface IUserFormValues {
    firstName: string;
    lastName: string;
    phoneNumber: string;
    jobTitle: string;
    userName: string;
    email: string;
    roles: string[];
    organizationId: string;
    address: string;
    city: string;
    state: string;
    zipCode: string;
    profileImageContent: string;
    profileImageMimeType: string;
    profileImageName: string;
    profileImageUrl: string;
}

const styles = () =>
    createStyles({
        fileInput: { display: 'none' },
        profileImage: {
            border: 'rgba(0,0,0,0.25) 1px solid',
            height: 60,
            marginRight: 20,
            width: 60,
        },
    });
const UserSchema = Yup.object().shape({
    email: Yup.string()
        .email('Not a valid email')
        .required('Email is Required'),
    firstName: Yup.string().required('First Name is Required'),
    lastName: Yup.string().required('Last Name is Required'),
    userName: Yup.string().required('Username is Required'),
});

type ISetFieldValue = (field: string, value: any) => void;

export interface IUserDialogProps extends WithStyles<typeof styles> {
    checkUsernameAvailability?: (user: IUser) => Promise<boolean>;
    checkEmailAvailability?: (user: IUser) => Promise<boolean>;
    close?: () => void;
    currentUser?: IUser;
    getOrganizations?: (pageNumber?: number, pageSize?: number) => void;
    getRoles?: (filter: string, pageNumber?: number, pageSize?: number) => Promise<void>;
    isAdmin?: boolean;
    isLoading?: boolean;
    isOpen?: boolean;
    isOrganizationAdmin?: boolean;
    organizations?: IOrganization[];
    roles?: IRole[];
    saveUser?: (user: IUser, imageHasChanged: boolean) => Promise<void>;
    user?: IUser;
}

class UserDialog extends React.Component<IUserDialogProps> {
    public isCancelled: boolean;
    public state = {
        imageHasChanged: false,
        isFileLoading: false,
    };
    public upload: HTMLInputElement | null;

    public debouncedValidateUsernameAvailability = AwesomeDebouncePromise((value: IUser) => {
        return this.props.checkUsernameAvailability!(value).then((data: boolean) => {
            if (!data) {
                throw 'Username Already In Use';
            }
        });
    }, 1000);

    public debouncedValidateEmailAvailability = AwesomeDebouncePromise((value: IUser) => {
        return this.props.checkEmailAvailability!(value).then((data: boolean) => {
            if (!data) {
                throw 'Email Already In Use';
            }
        });
    }, 500);

    public handleEmailChange = (email: string) => {
        if (email !== '') {
            const userDto = this.props.user!;
            userDto.email = email;
            return this.debouncedValidateEmailAvailability(userDto);
        }
    };

    public handleUsernameChange = (username: string) => {
        if (username !== '') {
            const userDto = this.props.user!;
            userDto.userName = username;
            return this.debouncedValidateUsernameAvailability(userDto);
        }
    };

    public componentDidMount() {
        this.props.getOrganizations!(undefined, 5000);
        this.props.getRoles!('', 1, 5000);
    }

    public save = (values: IUserFormValues, { setSubmitting }: FormikActions<IUserFormValues>) => {
        const userToSave = {
            ...values,
        } as IUser;
        delete userToSave.organization;
        this.props.saveUser!(userToSave, this.state.imageHasChanged).then(() => {
            setSubmitting(false);
        });
    };

    public handleOnButtonClick = (target: UserDialog) => {
        return () => target.upload && target.upload.click();
    };

    public handleFileChange = (setFieldValue: ISetFieldValue) => (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        if (event.target.files && event.target.files[0]) {
            const file = event.target.files[0];
            const reader = new FileReader();
            reader.onloadstart = () => {
                this.setState({ isFileLoading: true });
            };
            reader.onload = () => {
                if (reader) {
                    const imageContent = (reader.result! as string).split(',')[1];
                    const imageMimeType = file.type.split('/')[1];
                    const imageName = file.name;
                    const imageUrl = reader.result;
                    this.setState({
                        imageHasChanged: true,
                        isFileLoading: false,
                    });

                    setFieldValue('profileImageContent', imageContent);
                    setFieldValue('profileImageMimeType', imageMimeType);
                    setFieldValue('profileImageName', imageName);
                    setFieldValue('profileImageUrl', imageUrl);
                }
            };
            reader.readAsDataURL(file);
        }
    };

    public getOrganizationName(organization: IOrganization) {
        return organization.name;
    }

    public getOrganizationValue(organization: IOrganization) {
        return organization.id;
    }

    public getUserOrganizationName(user: IUser) {
        if (user.organization) {
            return this.getOrganizationName({
                ...user.organization,
                description: '',
            } as IOrganization);
        }
        return undefined;
    }

    public render() {
        const {
            user,
            currentUser,
            classes,
            close,
            isAdmin,
            isLoading,
            isOpen,
            isOrganizationAdmin,
            roles,
        } = this.props;
        const initialValues = { ...user } as IUserFormValues;

        return (
            <ESDialog
                close={close!}
                initialValues={initialValues}
                isLoading={isLoading}
                open={isOpen!}
                save={this.save}
                title={user!.isNew ? 'Create User' : 'Edit User'}
                validationSchema={UserSchema}
            >
                {({ isSubmitting, setFieldValue, values }) => (
                    <Grid container spacing={24}>
                        <Grid item container direction="row" alignItems="center" xs={12}>
                            <Grid item>
                                <Avatar
                                    src={values.profileImageUrl}
                                    className={classes.profileImage}
                                />
                            </Grid>
                            <Grid item>
                                <Button
                                    component="label"
                                    disabled={isSubmitting}
                                    isLoading={this.state.isFileLoading}
                                    onClick={this.handleOnButtonClick(this)}
                                    variant="outlined"
                                >
                                    Change
                                </Button>
                                <input
                                    type="file"
                                    accept="image/*"
                                    className={classes.fileInput}
                                    onChange={this.handleFileChange(setFieldValue)}
                                    ref={(ref) => (this.upload = ref)}
                                    style={{ display: 'none' }}
                                />
                            </Grid>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FullWidthField
                                autoFocus
                                label="First Name"
                                name="firstName"
                                required
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FullWidthField label="Last Name" name="lastName" required />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FullWidthField label="Phone Number" name="phoneNumber" />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FullWidthField label="Job Title" name="jobTitle" />
                        </Grid>
                        <Grid item xs={12}>
                            <FullWidthField
                                label="Username"
                                name="userName"
                                validate={this.handleUsernameChange}
                                required
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <FullWidthField
                                label="Email"
                                name="email"
                                validate={this.handleEmailChange}
                                required
                            />
                        </Grid>
                        {this.renderRolesField(
                            isAdmin,
                            isOrganizationAdmin,
                            user,
                            currentUser,
                            roles,
                            values
                        )}
                    </Grid>
                )}
            </ESDialog>
        );
    }

    public getRoleName(role: IRole) {
        return role.name || '';
    }

    private renderRolesField(
        isAdmin: boolean | undefined,
        isOrganizationAdmin: boolean | undefined,
        user: IUser | undefined,
        currentUser: IUser | undefined,
        roles: IRole[] | undefined,
        values: IUserFormValues
    ) {
        const hasValue = (field: string, formValues: IUserFormValues) => {
            const value = getIn(formValues, field) as any[];

            return value.length > 0;
        };

        if (user && currentUser && user.id === currentUser.id) {
            return null;
        }
        if (
            isAdmin ||
            (currentUser &&
                user &&
                isOrganizationAdmin &&
                this.getUserOrganizationName(currentUser) === this.getUserOrganizationName(user))
        ) {
            return (
                <Grid item xs={12}>
                    <SelectField
                        getName={this.getRoleName}
                        getValue={this.getRoleName}
                        inputId="roles"
                        items={roles || []}
                        checkedItems={values.roles}
                        label="Roles"
                        name="roles"
                        fullWidth
                        cypressLabel="roles"
                        childCypressLabel="role"
                        multiple
                        shrinkLabel={hasValue('roles', values)}
                    />
                </Grid>
            );
        }
        return null;
    }
}

const InjectedUserDialog = inject<IStores, IUserDialogProps, Partial<IUserDialogProps>, any>(
    (stores: IStores) => ({
        checkEmailAvailability: stores.users.checkEmailAvailability,
        checkUsernameAvailability: stores.users.checkUsernameAvailability,
        close: stores.users.closeDialog,
        currentUser: stores.global.user,
        getOrganizations: stores.organizations.getAllOrganizations,
        getRoles: stores.roles.getAllRoles,
        isAdmin: stores.global.isAdmin,
        isLoading: stores.users.isLoading,
        isOpen: stores.users.isModalOpen,
        isOrganizationAdmin: stores.global.isOrganizationAdmin,
        organizations: stores.organizations.organizations,
        roles: stores.roles.roles,
        saveUser: stores.users.saveUser,
        user: stores.users.selectedUser,
    })
)(withStyles(styles)(UserDialog));

export default InjectedUserDialog;



// WEBPACK FOOTER //
// ./src/users/UserDialog.tsx